import { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { DataProviderInstance } from '../../../api/DataProvider';
import { parseMoney, formatMoneyInput, cleanMoneyTyping } from '../../../utils/moneyUtils';

const emptyForm = () => ({
    type: 'expense',
    description: '',
    // Texto do campo como a pessoa digita ("1.200,99"); vira número só ao salvar
    amount: '',
    transactionDate: format(new Date(), 'yyyy-MM-dd'),
    categoryId: '',
    isRecurring: false,
    recurrenceFrequency: 'monthly',
    recurrenceStartDate: '',
    recurrenceEndDate: null,
    hasEndDate: false
});

/** "Esse mês" inteiro, do dia 1 ao último dia (o mesmo período de Leads). */
const lastDayOfMonth = () => {
    const today = new Date();
    return format(new Date(today.getFullYear(), today.getMonth() + 1, 0), 'yyyy-MM-dd');
};

/** Estado e ações da aba Transações (lista, filtros, recorrências e formulário). */
export default function useTransactions() {
    const {
        GetFinancialTransactions, GetActiveRecurringTransactions,
        GetFinancialCategories,
        SetFinancialTransaction,
        UpdateFinancialTransaction,
        DeleteFinancialTransaction
    } = DataProviderInstance();

    const [isLoading, setIsLoading] = useState(true);
    // A primeira carga mostra a página inteira em esqueleto; as seguintes só a lista
    const [hasLoaded, setHasLoaded] = useState(false);
    const [transactions, setTransactions] = useState([]);
    const [totals, setTotals] = useState(null);
    const [recurringTransactions, setRecurringTransactions] = useState([]);
    const [categories, setCategories] = useState([]);

    const [filters, setFilters] = useState({
        startDate: format(new Date(), 'yyyy-MM-01'),
        endDate: lastDayOfMonth(),
        type: 'all',
        categoryId: 'all'
    });

    const [formData, setFormData] = useState(emptyForm);
    const [showForm, setShowForm] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

    const showToast = useCallback((message, severity = 'success') => {
        setToast({ open: true, message, severity });
    }, []);

    const closeToast = useCallback(() => {
        setToast(prev => ({ ...prev, open: false }));
    }, []);

    const loadRecurringTransactions = useCallback(() => {
        GetActiveRecurringTransactions((response) => {
            if (response.status == 'success') {
                setRecurringTransactions(response.data);
            }
        });
    }, [GetActiveRecurringTransactions]);

    const loadTransactions = useCallback(() => {
        setIsLoading(true);
        GetFinancialTransactions(
            filters.startDate,
            filters.endDate,
            filters.type == 'all' ? '' : filters.type,
            filters.categoryId == 'all' ? '' : filters.categoryId,
            (response) => {
                setIsLoading(false);
                setHasLoaded(true);
                if (response.status == 'success') {
                    setTransactions(response.data.transactions);
                    setTotals(response.data.totals || null);
                }
            }
        );
    }, [GetFinancialTransactions, filters]);

    const loadCategories = useCallback(() => {
        GetFinancialCategories((response) => {
            if (response.status == 'success') {
                setCategories(response.data);
            }
        });
    }, [GetFinancialCategories]);

    const handleFilterChange = useCallback((e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    }, []);

    const handlePeriodChange = useCallback((startDate, endDate) => {
        setFilters(prev => ({ ...prev, startDate, endDate }));
    }, []);

    const handleFormChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => {
            const newData = {
                ...prev,
                [name]: type == 'checkbox' ? checked : (name == 'amount' ? cleanMoneyTyping(value) : value)
            };

            if (name == 'isRecurring') {
                if (checked) {
                    // A recorrência começa na data que já estava escolhida
                    newData.recurrenceStartDate = prev.transactionDate || format(new Date(), 'yyyy-MM-dd');
                } else {
                    newData.recurrenceStartDate = '';
                    newData.recurrenceEndDate = '';
                }
            }

            return newData;
        });
    }, []);

    // Ao sair do campo, o valor aparece com duas casas ("1.200,99")
    const handleAmountBlur = useCallback(() => {
        setFormData(prev => {
            const parsed = parseMoney(prev.amount);
            if (prev.amount === '' || parsed === null) return prev;
            return { ...prev, amount: formatMoneyInput(parsed) };
        });
    }, []);

    const handleDateChange = useCallback((date) => {
        setFormData(prev => ({ ...prev, transactionDate: date.format('YYYY-MM-DD') }));
    }, []);

    const handleRecurrenceStartDateChange = useCallback((date) => {
        setFormData(prev => ({ ...prev, recurrenceStartDate: date.format('YYYY-MM-DD') }));
    }, []);

    const handleRecurrenceEndDateChange = useCallback((date) => {
        setFormData(prev => ({ ...prev, recurrenceEndDate: date ? date.format('YYYY-MM-DD') : null }));
    }, []);

    const handleHasEndDateChange = useCallback((e) => {
        setFormData(prev => ({
            ...prev,
            hasEndDate: e.target.checked,
            recurrenceEndDate: e.target.checked ? prev.recurrenceEndDate : null
        }));
    }, []);

    const resetForm = useCallback(() => {
        setFormData(emptyForm());
        setIsEditing(false);
        setEditingId(null);
        setShowForm(false);
    }, []);

    const openNewForm = useCallback(() => setShowForm(true), []);

    const parsedAmount = parseMoney(formData.amount);
    const amountError = formData.amount !== '' && parsedAmount === null ? 'Use o formato 1.200,99' : '';

    // O que ainda falta para salvar, na ordem do formulário (vazio = pode salvar)
    const missing = [
        !String(formData.description || '').trim() && 'a descrição',
        !(parsedAmount > 0) && 'o valor',
        !(formData.isRecurring ? formData.recurrenceStartDate : formData.transactionDate) && 'a data',
        !formData.categoryId && 'a categoria',
        formData.isRecurring && formData.hasEndDate && !formData.recurrenceEndDate && 'a data de término'
    ].filter(Boolean);

    const validateForm = useCallback(() => missing.length === 0, [missing.length]);

    const handleSubmit = useCallback(() => {
        const data = {
            ...formData,
            amount: parseMoney(formData.amount),
            // Na recorrência a transação original cai na data de início (o campo "Data" some)
            transactionDate: formData.isRecurring ? formData.recurrenceStartDate : formData.transactionDate,
            categoryId: parseInt(formData.categoryId),
            recurrenceEndDate: formData.isRecurring && !formData.hasEndDate ? null : formData.recurrenceEndDate
        };

        const callback = (response) => {
            if (response.status == 'success') {
                loadTransactions();
                loadRecurringTransactions();
                resetForm();
                showToast(isEditing ? 'Transação atualizada' : 'Transação criada');
            } else {
                showToast(response.message || 'Não foi possível salvar a transação. Tente novamente.', 'error');
            }
        };

        if (isEditing) {
            UpdateFinancialTransaction({ ...data, id: editingId }, callback);
        } else {
            SetFinancialTransaction(data, callback);
        }
    }, [formData, isEditing, editingId, UpdateFinancialTransaction, SetFinancialTransaction, loadTransactions, loadRecurringTransactions, resetForm, showToast]);

    const handleEdit = useCallback((transaction) => {
        const hasValidEndDate = transaction.recurrenceEndDate &&
            transaction.recurrenceEndDate != '0000-00-00' &&
            transaction.recurrenceEndDate != '0000-00-00 00:00:00';

        setFormData({
            type: transaction.type,
            description: transaction.description,
            amount: formatMoneyInput(transaction.amount),
            transactionDate: transaction.transactionDate,
            categoryId: transaction.categoryId.toString(),
            isRecurring: transaction.isRecurring,
            recurrenceFrequency: transaction.recurrenceFrequency || 'monthly',
            recurrenceStartDate: transaction.recurrenceStartDate || transaction.transactionDate,
            recurrenceEndDate: hasValidEndDate ? transaction.recurrenceEndDate : null,
            hasEndDate: hasValidEndDate
        });
        setIsEditing(true);
        setEditingId(transaction.id);
        setShowForm(true);
    }, []);

    // Guarda a transação inteira para a confirmação citar descrição e valor
    const handleDelete = useCallback((transaction) => {
        setDeleteTarget(transaction);
    }, []);

    const cancelDelete = useCallback(() => {
        setDeleteTarget(null);
    }, []);

    const confirmDelete = useCallback(() => {
        if (deleteTarget) {
            DeleteFinancialTransaction(deleteTarget.id, (response) => {
                if (response.status == 'success') {
                    loadTransactions();
                    loadRecurringTransactions();
                    showToast('Transação excluída');
                } else {
                    showToast(response.message || 'Não foi possível excluir a transação. Tente novamente.', 'error');
                }
            });
        }
        setDeleteTarget(null);
    }, [deleteTarget, DeleteFinancialTransaction, loadTransactions, loadRecurringTransactions, showToast]);

    useEffect(() => {
        loadTransactions();
        loadCategories();
        loadRecurringTransactions();
    }, [loadTransactions, loadCategories, loadRecurringTransactions]);

    useEffect(() => {
        loadTransactions();
    }, [filters, loadTransactions]);

    return {
        isLoading, hasLoaded, transactions, totals, recurringTransactions, categories,
        filters, handleFilterChange, handlePeriodChange,
        formData, showForm, isEditing, openNewForm, resetForm, validateForm, missing, amountError, handleSubmit,
        handleFormChange, handleAmountBlur, handleDateChange, handleRecurrenceStartDateChange,
        handleRecurrenceEndDateChange, handleHasEndDateChange,
        handleEdit, handleDelete, deleteTarget, showDeleteConfirm: !!deleteTarget, confirmDelete, cancelDelete,
        toast, closeToast
    };
}
